import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Icon,
  ConfirmModal,
  FormDrawer,
} from "../../../Application/Admin/TeamsGroups/sharedComponents";
import "./Broadcast.scss";
import {
  getBroadcastsApi,
  createBroadcastApi,
  updateBroadcastApi,
  updateBroadcastStatusApi,
  deleteBroadcastApi,
  getBroadcastTemplatesApi,
  getDepartmentsApi,
  getTeamsApi,
  getUsersApi,
  getPresignedUploadUrlApi,
  registerMediaApi,
} from "../../../../api/api";

const BROADCAST_TABS = ["All Broadcasts", "Approvals", "Templates", "Reports"];
const BROADCAST_TYPE_OPTIONS = [
  { value: "circular", label: "Circular" },
  { value: "announcement", label: "Announcement" },
  { value: "alert", label: "Alert" },
  { value: "reminder", label: "Reminder" },
  { value: "emergency", label: "Emergency" },
];
const TARGET_OPTIONS = [
  { value: "all_users", label: "All Users" },
  { value: "department", label: "Specific Department" },
  { value: "team", label: "Specific Team" },
  { value: "channel", label: "Specific Channel" },
  { value: "selected_users", label: "Selected Users" },
];
const ATTACHMENT_TYPES = [
  { value: "", label: "None" },
  { value: "pdf", label: "PDF Document" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
];

// ─── Action Dropdown Component ───────────────────────────────────────────
function ActionDropdown({
  broadcast,
  onEdit,
  onSubmit,
  onApprove,
  onPublish,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="action-dropdown" ref={ref}>
      <button
        className="action-dropdown__trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="action-dropdown__menu">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(broadcast);
              setOpen(false);
            }}
          >
            <Icon name="edit" size={14} /> Edit
          </button>
          {broadcast.status === "draft" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSubmit(broadcast);
                setOpen(false);
              }}
            >
              <Icon name="chevronRight" size={14} /> Submit for Approval
            </button>
          )}
          {broadcast.status === "pending_approval" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onApprove(broadcast);
                setOpen(false);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>{" "}
              Approve
            </button>
          )}
          {broadcast.status === "approved" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPublish(broadcast);
                setOpen(false);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>{" "}
              Publish
            </button>
          )}
          {(broadcast.status === "draft" ||
            broadcast.status === "pending_approval" ||
            broadcast.status === "cancelled") && (
            <>
              <div
                style={{
                  height: "1px",
                  background: "#e5e7eb",
                  margin: "4px 8px",
                }}
              />
              <button
                style={{ color: "#ef4444" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(broadcast);
                  setOpen(false);
                }}
              >
                <Icon name="trash" size={14} /> Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Broadcast Component ────────────────────────────────────────────
function Broadcast() {
  const navigate = useNavigate();
  const { layout } = useParams();
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("All Broadcasts");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // ─── Fetch Data ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchBroadcasts();
    fetchTemplates();
    fetchDepartments();
    fetchTeams();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const fetchBroadcasts = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await getBroadcastsApi(
        filters.page || 1,
        filters.limit || 50,
        filters.search || "",
        filters.status || "",
        filters.type || "",
        filters.priority || "",
      );
      if (res.data.success) setBroadcasts(res.data.broadcasts);
    } catch (err) {
      console.error("Fetch broadcasts error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await getBroadcastTemplatesApi();
      if (res.data.success) setTemplates(res.data.templates);
    } catch (err) {
      console.error("Fetch templates error:", err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await getDepartmentsApi();
      if (res.data.success) setDepartments(res.data.departments || []);
    } catch (err) {
      console.error("Fetch departments error:", err);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await getTeamsApi(1, 100);
      if (res.data.success) setTeams(res.data.teams || []);
    } catch (err) {
      console.error("Fetch teams error:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await getUsersApi(1, 200, "");
      if (res.data.success) setUsers(res.data.users || []);
    } catch (err) {
      console.error("Fetch users error:", err);
    }
  };

  // ─── Upload File Immediately with Progress ─────────────────────────────
  const uploadFileImmediately = async (file, updateField) => {
    try {
      // Step 1: Get presigned URL
      updateField("uploadProgress", 10);
      const presignedRes = await getPresignedUploadUrlApi({
        fileName: file.name,
        mimeType: file.type,
        folder: "broadcast-attachments",
      });

      if (!presignedRes.data.success) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, objectKey, fileUrl } = presignedRes.data;
      updateField("uploadProgress", 30);

      // Step 2: Upload file directly to MinIO with progress tracking
      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 70) / progressEvent.total + 30,
          );
          updateField("uploadProgress", percentCompleted);
        },
      });

      updateField("uploadProgress", 85);

      // Step 3: Register the media in database
      await registerMediaApi({
        fileName: file.name,
        mimeType: file.type,
        objectKey,
        publicUrl: fileUrl,
        fileSize: file.size,
        sourceType: "BROADCAST",
        matrixRoomId: "broadcast",
      });

      updateField("uploadProgress", 100);

      // Set the uploaded attachment data
      const attachmentType = file.type.startsWith("image/")
        ? "image"
        : file.type === "application/pdf"
          ? "pdf"
          : file.type.startsWith("video/")
            ? "video"
            : file.type.startsWith("audio/")
              ? "audio"
              : "other";

      updateField("attachmentUrl", fileUrl);
      updateField("attachmentType", attachmentType);
      updateField("attachmentName", file.name);
      updateField("uploading", false);
      updateField("uploadComplete", true);

      return {
        attachmentUrl: fileUrl,
        attachmentType,
        attachmentName: file.name,
      };
    } catch (err) {
      console.error("Upload attachment error:", err);
      updateField("uploading", false);
      updateField("uploadComplete", false);
      updateField("uploadProgress", 0);
      updateField("attachmentFile", null);
      updateField("attachmentName", "");
      updateField("attachmentUrl", "");
      alert("Failed to upload file. Please try again.");
      throw err;
    }
  };

  // ─── Broadcast Form Fields ───────────────────────────────────────────────
  const getBroadcastFields = (departments, teams, users) => [
    {
      name: "broadcastType",
      label: "Broadcast Type",
      type: "select",
      options: BROADCAST_TYPE_OPTIONS,
      defaultValue: "circular",
    },
    {
      name: "priority",
      label: "Priority",
      type: "select",
      options: [
        { value: "normal", label: "Normal" },
        { value: "important", label: "Important" },
        { value: "critical", label: "Critical" },
      ],
      defaultValue: "normal",
    },
    {
      name: "title",
      label: "Title",
      type: "text",
      placeholder: "e.g. System Maintenance Notice",
      required: true,
    },
    {
      name: "body",
      label: "Message Body",
      type: "textarea",
      rows: 4,
      placeholder: "Enter the broadcast message...",
      required: true,
    },
    {
      name: "attachmentType",
      label: "Attachment Type",
      type: "select",
      options: ATTACHMENT_TYPES,
      defaultValue: "",
    },
    {
      name: "attachmentFile",
      label: "Upload Attachment",
      type: "custom",
      visible: (form) => form.attachmentType && form.attachmentType !== "",
      render: ({ form, updateField }) => (
        <div className="attachment-upload">
          {form.attachmentFile || form.attachmentUrl ? (
            <div className="attachment-upload__preview">
              <div className="attachment-upload__file-info">
                <Icon name="file" size={20} />
                <span>{form.attachmentFile?.name || form.attachmentName}</span>
                {(form.attachmentFile?.size || form.attachmentUrl) && (
                  <span className="attachment-upload__file-size">
                    {form.attachmentFile?.size
                      ? `(${(form.attachmentFile.size / 1024 / 1024).toFixed(2)} MB)`
                      : ""}
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              {form.uploading && (
                <div className="upload-progress">
                  <div className="upload-progress__bar">
                    <div
                      className="upload-progress__fill"
                      style={{ width: `${form.uploadProgress || 0}%` }}
                    />
                  </div>
                  <span className="upload-progress__text">
                    {form.uploadProgress === 100
                      ? "Processing..."
                      : `Uploading... ${form.uploadProgress || 0}%`}
                  </span>
                </div>
              )}

              {/* Success Message */}
              {form.uploadComplete && (
                <div className="upload-success">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#22C55E"
                    strokeWidth="2"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>Uploaded successfully</span>
                </div>
              )}

              <button
                type="button"
                className="link-btn link-btn--danger"
                onClick={() => {
                  updateField("attachmentFile", null);
                  updateField("attachmentName", "");
                  updateField("attachmentUrl", "");
                  updateField("uploading", false);
                  updateField("uploadProgress", 0);
                  updateField("uploadComplete", false);
                }}
                disabled={form.uploading}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="attachment-upload__zone">
              <input
                type="file"
                id="broadcast-attachment"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    updateField("attachmentFile", file);
                    updateField("attachmentName", file.name);
                    updateField("uploading", true);
                    updateField("uploadProgress", 0);
                    updateField("uploadComplete", false);
                    uploadFileImmediately(file, updateField);
                  }
                }}
                accept={
                  form.attachmentType === "pdf"
                    ? ".pdf"
                    : form.attachmentType === "image"
                      ? "image/*"
                      : form.attachmentType === "video"
                        ? "video/*"
                        : form.attachmentType === "audio"
                          ? "audio/*"
                          : "*/*"
                }
                style={{ display: "none" }}
              />
              <label
                htmlFor="broadcast-attachment"
                className="attachment-upload__label"
              >
                <Icon name="plus" size={16} />
                <span>
                  Choose{" "}
                  {
                    ATTACHMENT_TYPES.find(
                      (t) => t.value === form.attachmentType,
                    )?.label
                  }{" "}
                  File
                </span>
              </label>
              <span className="field-hint">
                {form.attachmentType === "pdf" && "PDF up to 10MB"}
                {form.attachmentType === "image" && "JPG, PNG, GIF up to 5MB"}
                {form.attachmentType === "video" && "MP4, MOV up to 50MB"}
                {form.attachmentType === "audio" && "MP3, WAV up to 10MB"}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "targetType",
      label: "Target Audience",
      type: "select",
      options: TARGET_OPTIONS,
      defaultValue: "all_users",
    },
    {
      name: "targetDetails",
      label: "",
      type: "custom",
      visible: (form) => form.targetType && form.targetType !== "all_users",
      render: ({ form, updateField }) => (
        <div className="target-details">
          {form.targetType === "department" && (
            <div className="form-group">
              <label>Select Department</label>
              <select
                value={form.targetDepartment || ""}
                onChange={(e) => {
                  updateField("targetDepartment", e.target.value);
                }}
              >
                <option value="">Select department</option>
                {departments
                  .filter((d) => d.name !== "All")
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {form.targetType === "team" && (
            <div className="form-group">
              <label>Select Team</label>
              <select
                value={form.targetTeam || ""}
                onChange={(e) => {
                  updateField("targetTeam", e.target.value);
                }}
              >
                <option value="">Select team</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {form.targetType === "channel" && (
            <div className="form-group">
              <label>Select Channel</label>
              <select
                value={form.targetChannel || ""}
                onChange={(e) => {
                  updateField("targetChannel", e.target.value);
                }}
              >
                <option value="">Select channel</option>
                <option value="general">General</option>
                <option value="announcements">Announcements</option>
                <option value="updates">Updates</option>
                <option value="alerts">Alerts</option>
              </select>
            </div>
          )}
          {form.targetType === "selected_users" && (
            <div className="form-group">
              <label>Select Users</label>
              <div className="member-picker">
                {users.map((user) => {
                  const isChecked = (form.targetUserIds || []).includes(
                    user.id,
                  );
                  return (
                    <label key={user.id} className="member-picker__item">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          const ids = form.targetUserIds || [];
                          updateField(
                            "targetUserIds",
                            isChecked
                              ? ids.filter((id) => id !== user.id)
                              : [...ids, user.id],
                          );
                        }}
                      />
                      <span>{user.fullName}</span>
                      <span className="member-picker__dept">
                        {user.department?.name || "N/A"}
                      </span>
                    </label>
                  );
                })}
              </div>
              <span className="field-hint">
                {(form.targetUserIds || []).length} user(s) selected
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "scheduleTime",
      label: "Schedule (optional)",
      type: "datetime-local",
    },
    {
      name: "oneTimeView",
      label: "One-Time View (Disappears after reading)",
      type: "toggle",
      defaultValue: false,
    },
    {
      name: "acknowledgementRequired",
      label: "Acknowledgement Required",
      type: "toggle",
      defaultValue: false,
    },
  ];

  // ─── CRUD Handlers ─────────────────────────────────────────────────────
  const handleCreateBroadcast = async (formData) => {
    try {
      const payload = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        broadcastType: formData.broadcastType,
        priority: formData.priority,
        targetType: formData.targetType,
        targetName:
          formData.targetType === "all_users"
            ? "All Users"
            : formData.targetDepartment ||
              formData.targetTeam ||
              formData.targetChannel ||
              "",
        targetDepartment: formData.targetDepartment || null,
        targetTeam: formData.targetTeam || null,
        targetChannel: formData.targetChannel || null,
        targetUserIds: formData.targetUserIds || [],
        scheduleTime: formData.scheduleTime || null,
        oneTimeView: formData.oneTimeView || false,
        acknowledgementRequired: formData.acknowledgementRequired || false,
        attachmentUrl: formData.attachmentUrl || "",
        attachmentType: formData.attachmentType || "",
        attachmentName: formData.attachmentName || "",
        status: "draft",
      };

      const res = await createBroadcastApi(payload);
      if (res.data.success) {
        setToast(`Broadcast "${payload.title}" created as draft.`);
        fetchBroadcasts();
        closeDrawer();
      }
    } catch (err) {
      console.error("Create broadcast error:", err);
      setToast("Failed to create broadcast");
    }
  };

  const handleEditBroadcast = async (formData) => {
    try {
      const payload = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        broadcastType: formData.broadcastType,
        priority: formData.priority,
        targetType: formData.targetType,
        targetDepartment: formData.targetDepartment || null,
        targetTeam: formData.targetTeam || null,
        targetChannel: formData.targetChannel || null,
        targetUserIds: formData.targetUserIds || [],
        scheduleTime: formData.scheduleTime || null,
        oneTimeView: formData.oneTimeView || false,
        acknowledgementRequired: formData.acknowledgementRequired || false,
        attachmentUrl:
          formData.attachmentUrl || editingBroadcast?.attachmentUrl || "",
        attachmentType:
          formData.attachmentType || editingBroadcast?.attachmentType || "",
        attachmentName:
          formData.attachmentName || editingBroadcast?.attachmentName || "",
      };

      const res = await updateBroadcastApi(editingBroadcast.id, payload);
      if (res.data.success) {
        setToast(`Broadcast "${payload.title}" updated.`);
        fetchBroadcasts();
        closeDrawer();
      }
    } catch (err) {
      console.error("Update broadcast error:", err);
      setToast("Failed to update broadcast");
    }
  };

  const handleSubmitForApproval = async (broadcast) => {
    try {
      await updateBroadcastStatusApi(broadcast.id, {
        status: "pending_approval",
      });
      setToast("Broadcast submitted for approval.");
      fetchBroadcasts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleApprove = async (broadcast) => {
    try {
      await updateBroadcastStatusApi(broadcast.id, { status: "approved" });
      setToast("Broadcast approved.");
      fetchBroadcasts();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePublish = async (broadcast) => {
    try {
      await updateBroadcastStatusApi(broadcast.id, { status: "published" });
      setToast("Broadcast published successfully.");
      fetchBroadcasts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteBroadcast = (broadcast) => {
    setConfirm({
      title: "Delete Broadcast",
      message: `Are you sure you want to delete "${broadcast.title}"? This action cannot be undone.`,
      confirmLabel: "Yes, Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await deleteBroadcastApi(broadcast.id);
          setToast(`Broadcast "${broadcast.title}" deleted successfully.`);
          fetchBroadcasts();
        } catch (err) {
          console.error("Delete broadcast error:", err);
          setToast("Failed to delete broadcast");
        }
        setConfirm(null);
      },
    });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingBroadcast(null);
    setDrawerMode("create");
  };

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setEditingBroadcast(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (broadcast) => {
    setDrawerMode("edit");
    setEditingBroadcast(broadcast);
    setDrawerOpen(true);
  };

  // ─── Filtered Data ─────────────────────────────────────────────────────
  const filteredBroadcasts = useMemo(() => {
    if (activeTab === "All Broadcasts") return broadcasts;
    if (activeTab === "Approvals")
      return broadcasts.filter((b) => b.status === "pending_approval");
    return [];
  }, [broadcasts, activeTab]);

  // ─── Badge Helpers ─────────────────────────────────────────────────────
  const getTypeBadgeClass = (type) =>
    ({
      circular: "badge--type-circular",
      announcement: "badge--type-announcement",
      alert: "badge--type-alert",
      reminder: "badge--type-reminder",
      emergency: "badge--type-emergency",
    })[type] || "";

  const getPriorityBadgeClass = (p) =>
    ({
      normal: "badge--priority-normal",
      important: "badge--priority-important",
      critical: "badge--priority-critical",
    })[p] || "";

  const getStatusBadgeClass = (s) =>
    ({
      draft: "badge--status-draft",
      pending_approval: "badge--status-pending_approval",
      approved: "badge--status-approved",
      published: "badge--status-published",
      scheduled: "badge--status-scheduled",
      cancelled: "badge--status-cancelled",
    })[s] || "";

  const drawerConfig =
    drawerMode === "edit" && editingBroadcast
      ? {
          title: "Edit Broadcast",
          subtitle: `Update: ${editingBroadcast.title}`,
          editMode: true,
          initialData: editingBroadcast,
          submitLabel: "Save Changes",
        }
      : {
          title: "Create Broadcast",
          subtitle:
            "Send circular, announcement, alert, reminder, or emergency broadcast.",
          editMode: false,
          initialData: {},
          submitLabel: "Create Broadcast",
        };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="broadcasts-page">
      <div className="broadcasts-header">
        <div>
          <h1 className="broadcasts-header__title">Broadcasts</h1>
          <p className="broadcasts-header__subtitle">
            Manage circulars, announcements, alerts, reminders, and emergency
            messages.
          </p>
        </div>
        <div className="broadcasts-header__actions">
          <button
            type="button"
            className="btn-create"
            onClick={openCreateDrawer}
          >
            <span className="btn-create__icon">+</span>Create Broadcast
          </button>
        </div>
      </div>

      <div className="broadcasts-tabs">
        {BROADCAST_TABS.map((tab) => (
          <button
            key={tab}
            className={`broadcasts-tabs__item ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {(activeTab === "All Broadcasts" || activeTab === "Approvals") && (
        <div className="broadcasts-table-card">
          {loading ? (
            <div className="empty-state">
              <p>Loading broadcasts...</p>
            </div>
          ) : (
            <table className="broadcasts-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Target</th>
                  <th>One-Time</th>
                  <th>Attachment</th>
                  <th>Created</th>
                  <th>Date</th>
                  <th style={{ width: "50px" }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredBroadcasts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="broadcasts-table__empty">
                      No broadcasts found.
                    </td>
                  </tr>
                ) : (
                  filteredBroadcasts.map((b) => (
                    <tr
                      key={b.id}
                      onClick={() => navigate(`/broadcasts/${b.id}/${layout}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="broadcasts-table__title">{b.title}</td>
                      <td>
                        <span
                          className={`badge ${getTypeBadgeClass(b.broadcastType)}`}
                        >
                          {b.broadcastType}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${getPriorityBadgeClass(b.priority)}`}
                        >
                          {b.priority}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${getStatusBadgeClass(b.status)}`}
                        >
                          {b.status?.replace("_", " ")}
                        </span>
                      </td>
                      <td>{b.targetName}</td>
                      <td>
                        {b.oneTimeView ? (
                          <span className="badge badge--type-emergency">
                            One-Time
                          </span>
                        ) : (
                          <span className="badge badge--status-draft">
                            Normal
                          </span>
                        )}
                      </td>
                      <td>
                        {b.attachmentName ? (
                          <span style={{ fontSize: "12px", color: "#0062FE" }}>
                            📎 {b.attachmentName}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{b.createdBy}</td>
                      <td>{new Date(b.createdAt).toLocaleDateString()}</td>
                      <td
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: "relative" }}
                      >
                        <ActionDropdown
                          broadcast={b}
                          onEdit={openEditDrawer}
                          onSubmit={handleSubmitForApproval}
                          onApprove={handleApprove}
                          onPublish={handlePublish}
                          onDelete={handleDeleteBroadcast}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "Templates" && (
        <div className="broadcasts-table-card">
          <table className="broadcasts-table">
            <thead>
              <tr>
                <th>Template Name</th>
                <th>Type</th>
                <th>Title</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td className="broadcasts-table__title">{t.name}</td>
                  <td>
                    <span className={`badge ${getTypeBadgeClass(t.type)}`}>
                      {t.type}
                    </span>
                  </td>
                  <td>{t.title}</td>
                  <td>
                    <span
                      className={`badge ${getPriorityBadgeClass(t.priority)}`}
                    >
                      {t.priority}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "Reports" && (
        <div className="broadcasts-table-card">
          <div className="empty-state">
            <p>Reports available soon.</p>
          </div>
        </div>
      )}

      {drawerOpen && (
        <FormDrawer
          open={drawerOpen}
          editMode={drawerConfig.editMode}
          initialData={drawerConfig.initialData}
          title={drawerConfig.title}
          subtitle={drawerConfig.subtitle}
          fields={getBroadcastFields(departments, teams, users)}
          onClose={closeDrawer}
          submitLabel={drawerConfig.submitLabel}
          onSubmit={
            drawerMode === "edit" ? handleEditBroadcast : handleCreateBroadcast
          }
          validate={(f) => {
            if (!f.title?.trim()) return "Title required";
            if (!f.body?.trim()) return "Body required";
            return null;
          }}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
      {toast && <div className="broadcasts-toast">{toast}</div>}
    </div>
  );
}

export default Broadcast;
